import React, { useState, useEffect, useRef } from 'react';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import {
    Bell,
    AlertTriangle,
    AlertOctagon,
    Info,
    X,
    CheckCheck,
    ExternalLink,
    PauseCircle,
    Activity,
    ClipboardList,
    ShieldAlert,
    Check,
    BellOff
} from 'lucide-react';
import { formatRelativeDistance } from '../lib/i18n';

const SEVERITY_CONFIG = {
    INFO: { fallbackIcon: Info, bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-500', dot: 'bg-blue-500' },
    WARN: { fallbackIcon: AlertTriangle, bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', text: 'text-yellow-500', dot: 'bg-yellow-500' },
    HIGH: { fallbackIcon: AlertOctagon, bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-500', dot: 'bg-orange-500' },
    CRITICAL: { fallbackIcon: AlertOctagon, bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-500', dot: 'bg-red-500' }
};

const EVENT_ICON_MAP = {
    'WORKFLOW_FAILED': Activity,
    'WORKFLOW_PAUSED': PauseCircle,
    'MANUAL_WAIT': ClipboardList,
    'SUCCESS_RISK_ESCALATED': ShieldAlert,
    'RENEWAL_DUE_SOON': AlertTriangle
};

/**
 * NotificationPanel — Bell icon with dropdown feed in the sidebar.
 */
export default function NotificationPanel({ isCollapsed }) {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('unread');
    const panelRef = useRef(null);

    // Grouping utility: collapse notifications with same eventType and entityId within 12h
    const groupNotifications = (notifs) => {
        const groups = [];
        const THRESHOLD_MS = 12 * 60 * 60 * 1000;

        notifs.forEach(n => {
            // Find existing group
            const existing = groups.find(g =>
                g.eventType === n.eventType &&
                g.entityId === n.entityId &&
                Math.abs(new Date(g.items[0].createdAt).getTime() - new Date(n.createdAt).getTime()) < THRESHOLD_MS
            );

            if (existing) {
                existing.items.push(n);
                // Keep the most recent timestamp as the top-level timestamp
                if (new Date(n.createdAt) > new Date(existing.createdAt)) {
                    existing.createdAt = n.createdAt;
                }
                // If any item in the group is unread, the group is unread
                if (!n.isRead) {
                    existing.isRead = false;
                }
            } else {
                groups.push({
                    id: n._id, // use first item's ID as key
                    eventType: n.eventType,
                    entityId: n.entityId,
                    entityType: n.entityType,
                    title: n.title,
                    message: n.message,
                    severity: n.severity,
                    route: n.route,
                    createdAt: n.createdAt,
                    isRead: n.isRead,
                    items: [n]
                });
            }
        });

        // Sort groups by newest first
        return groups.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    };

    // Token logic completely removed; the global 'api' client handles auth context.

    // Fetch unread count on mount and interval
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000); // every 30s
        return () => clearInterval(interval);
    }, []);

    // Close panel on outside click
    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const fetchUnreadCount = async () => {
        try {
            const res = await api.get(`/api/notifications/unread-count`);
            setUnreadCount(res.data.count || 0);
        } catch (_) { }
    };

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/api/notifications?limit=50`);
            setItems(res.data.items || []);
        } catch (_) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Default to 'unread' if there are unread items, else 'all'
            setActiveTab(unreadCount > 0 ? 'unread' : 'all');
            fetchNotifications();
        }
    };

    const markRead = async (id, itemsInGroup) => {
        try {
            if (itemsInGroup && itemsInGroup.length > 1) {
                const unreadItems = itemsInGroup.filter(it => !it.isRead);
                const ids = unreadItems.map(it => it._id);
                if (ids.length > 0) {
                    await api.put(`/api/notifications/read-bulk`, { ids });
                    setItems(prev => prev.map(it => ids.includes(it._id) ? { ...it, isRead: true, readAt: new Date() } : it));
                    setUnreadCount(c => Math.max(0, c - ids.length));
                }
            } else {
                await api.put(`/api/notifications/${id}/read`);
                setItems(prev => prev.map(it => it._id === id ? { ...it, isRead: true, readAt: new Date() } : it));
                setUnreadCount(c => Math.max(0, c - 1));
            }
        } catch (_) { }
    };

    const handleSnooze = async (group, e) => {
        e.stopPropagation(); // prevent route click
        try {
            await api.post(`/api/notifications/mute`, { entityId: group.entityId, durationHours: 24 });
            // Remove from the local feed state so it disappears instantly
            const groupIds = group.items.map(it => it._id);
            setItems(prev => prev.filter(it => !groupIds.includes(it._id)));
            const unreadInGroup = group.items.filter(it => !it.isRead).length;
            setUnreadCount(c => Math.max(0, c - unreadInGroup));
        } catch (_) { }
    };

    const markAllRead = async () => {
        try {
            await api.put(`/api/notifications/read-all`);
            setItems(prev => prev.map(it => ({ ...it, isRead: true, readAt: new Date() })));
            setUnreadCount(0);
        } catch (_) { }
    };

    const handleClick = (group, e) => {
        if (e) {
            e.stopPropagation();
        }

        if (!group.isRead) {
            markRead(group.id, group.items);
        }

        if (group.route && (!e || !e.target.closest('.mark-read-btn'))) {
            setIsOpen(false);
            const target = group.entityId ? `${group.route}?focus=${group.entityId}` : group.route;
            navigate(target);
        }
    };

    const handleMarkReadClick = (group, e) => {
        e.stopPropagation(); // Prevent row click routing
        if (!group.isRead) {
            markRead(group.id, group.items);
        }
    };

    // Filter and group
    const filteredItems = items.filter(it => activeTab === 'all' || !it.isRead);
    const groupedItems = groupNotifications(filteredItems);

    return (
        <div ref={panelRef} className="relative">
            {/* Bell button */}
            <button
                onClick={handleOpen}
                className={cn(
                    "relative h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground border border-transparent hover:border-border/50 hover:bg-accent/40 rounded-xl transition-all group",
                    isOpen && "bg-accent/40 border-border/50 text-foreground"
                )}
                title={t('notifications.title')}
            >
                <Bell className={cn("w-4 h-4 transition-colors", isOpen ? "text-foreground" : "group-hover:text-foreground")} />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold px-1 ring-2 ring-background animate-in zoom-in duration-300">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown panel */}
            {isOpen && (
                <div className="fixed inset-x-4 top-[72px] sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-[400px] bg-background/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-border/50 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 origin-top-right">
                    {/* Header */}
                    <div className="px-5 py-4 border-b border-border/50 flex items-center justify-between bg-muted/30">
                        <div className="flex items-center gap-2.5">
                            <div className="p-1.5 bg-primary/10 rounded-lg">
                                <Bell className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-foreground">{t('notifications.title')}</h3>
                                {unreadCount > 0 && (
                                    <p className="text-[10px] text-muted-foreground font-medium">
                                        {t('notifications.unreadSummary', { count: unreadCount })}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllRead}
                                    className="text-[11px] text-primary hover:text-primary/80 flex items-center gap-1.5 font-medium px-2.5 py-1.5 rounded-lg hover:bg-primary/10 transition-colors"
                                    title={t('notifications.markAllRead')}
                                >
                                    <CheckCheck className="w-3.5 h-3.5" />
                                    <span className="hidden xs:inline">{t('notifications.markAllRead')}</span>
                                </button>
                            )}
                            <button 
                                onClick={() => setIsOpen(false)} 
                                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex px-5 border-b border-border/50 bg-background/40">
                        <button
                            onClick={() => setActiveTab('unread')}
                            className={cn(
                                "px-4 py-2.5 text-xs font-medium border-b-2 transition-all relative",
                                activeTab === 'unread' ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                            )}
                        >
                            {t('notifications.unread')}
                            {unreadCount > 0 && (
                                <span className={cn(
                                    "ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-primary/10 text-primary",
                                    activeTab === 'unread' ? "bg-primary text-primary-foreground" : ""
                                )}>
                                    {unreadCount}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('all')}
                            className={cn(
                                "px-4 py-2.5 text-xs font-medium border-b-2 transition-all",
                                activeTab === 'all' ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                            )}
                        >
                            {t('notifications.all')}
                        </button>
                    </div>

                    {/* Items */}
                    <div className="max-h-[480px] overflow-y-auto scrollbar-thin scrollbar-thumb-border">
                        {loading && (
                            <div className="px-4 py-12 text-center flex flex-col items-center gap-3">
                                <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                <span className="text-xs text-muted-foreground animate-pulse">{t('common.loading')}</span>
                            </div>
                        )}

                        {!loading && groupedItems.length === 0 && (
                            <div className="px-6 py-16 text-center">
                                <div className="w-12 h-12 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4 border border-border/50">
                                    <BellOff className="w-6 h-6 text-muted-foreground/50" />
                                </div>
                                <div className="text-sm text-foreground font-semibold">
                                    {t('notifications.empty')}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1.5 max-w-[200px] mx-auto leading-relaxed">
                                    {t('notifications.emptyDescription')}
                                </div>
                                {activeTab === 'unread' && (
                                    <button 
                                        onClick={() => setActiveTab('all')}
                                        className="mt-6 text-xs text-primary hover:underline font-medium"
                                    >
                                        {t('notifications.all')}
                                    </button>
                                )}
                            </div>
                        )}

                        {!loading && groupedItems.map(group => {
                            const sev = SEVERITY_CONFIG[group.severity] || SEVERITY_CONFIG.INFO;
                            const SevIcon = EVENT_ICON_MAP[group.eventType] || sev.fallbackIcon;

                            // Style differences for read vs unread
                            const containerBg = group.isRead ? 'bg-transparent hover:bg-muted/30' : `${sev.bg} hover:brightness-95 dark:hover:brightness-110`;
                            const titleColor = group.isRead ? 'text-foreground/80 font-medium' : 'text-foreground font-bold';

                            return (
                                <div
                                    key={group.id}
                                    onClick={(e) => handleClick(group, e)}
                                    className={cn(
                                        "w-full text-left px-5 py-4 flex items-start gap-4 border-b border-border/30 transition-all cursor-pointer group/item relative overflow-hidden",
                                        containerBg
                                    )}
                                >
                                    {/* Unread indicator bar */}
                                    {!group.isRead && <div className={cn("absolute left-0 top-0 bottom-0 w-1", sev.dot)} />}

                                    {/* Action overlays (hover state) */}
                                    <div className="absolute right-3 top-4 opacity-0 group-hover/item:opacity-100 flex items-center gap-1.5 z-10 transition-all duration-200 translate-x-2 group-hover/item:translate-x-0">
                                        {/* Snooze button */}
                                        {group.entityId && (
                                            <button
                                                onClick={(e) => handleSnooze(group, e)}
                                                className="p-1.5 rounded-lg bg-background shadow-lg border border-border/50 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                                                title="Snooze for 24 hours"
                                            >
                                                <BellOff className="w-3.5 h-3.5" />
                                            </button>
                                        )}

                                        {/* Mark read button */}
                                        {!group.isRead && (
                                            <button
                                                onClick={(e) => handleMarkReadClick(group, e)}
                                                className="p-1.5 rounded-lg bg-background shadow-lg border border-border/50 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                                                title="Mark as read"
                                            >
                                                <Check className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Severity icon */}
                                    <div className={cn(
                                        "flex-shrink-0 w-9 h-9 rounded-xl border flex items-center justify-center mt-0.5 transition-transform group-hover/item:scale-110",
                                        sev.bg,
                                        sev.border
                                    )}>
                                        <SevIcon className={cn("w-4 h-4", sev.text)} />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0 pr-6">
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <div className={cn("text-sm truncate leading-none", titleColor)}>
                                                {group.title}
                                            </div>
                                            {group.items.length > 1 && (
                                                <span className={cn(
                                                    "text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-background/50 backdrop-blur-sm",
                                                    sev.border,
                                                    sev.text
                                                )}>
                                                    +{group.items.length - 1} more
                                                </span>
                                            )}
                                        </div>
                                        {group.message && (
                                            <div className={cn(
                                                "text-xs line-clamp-2 leading-relaxed mb-2",
                                                group.isRead ? 'text-muted-foreground/70' : 'text-muted-foreground'
                                            )}>
                                                {group.message}
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] text-muted-foreground/60 flex items-center gap-2">
                                                <span className="font-medium">{formatRelativeDistance(group.createdAt, {}, i18n.language)}</span>
                                                {group.route && (
                                                    <span className="flex items-center gap-1 text-primary/70 group-hover/item:text-primary transition-colors">
                                                        <ExternalLink className="w-2.5 h-2.5" />
                                                        <span>View Details</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
